import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { State } from "../../../../reducer";
import { postOrder } from "../../../../api/order.api";
import { getMenuByID } from "../../../../api/menu.api";
import { ikThumb } from "../../../../utils/image";
import { useEffect } from "react";

export const FormOrderDessert: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: State) => state.user);
  const order = useSelector((state: State) => state.order);
  const { menuName, menuID } = useParams();
  const menuIdAsNumber = menuID ? parseInt(menuID) : order.menu_id;

  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [sauce, setSauce] = useState<string>("");
  // Per-menu sauce list, loaded from the API. NULL until the menu fetch
  // resolves; empty list = no picker for this menu.
  const [sauces, setSauces] = useState<string[] | null>(null);

  const hasSauces = !!sauces && sauces.length > 0;

  const isAdmin = user?.role === "admin" || user?.role === "dev";
  const backTo = isAdmin ? "/menuDessert" : "/menuDessert";
  const queueTo = isAdmin ? "/statusOrder-admin" : "/statusOrder";

  useEffect(() => {
    if (!menuIdAsNumber) return;
    getMenuByID(menuIdAsNumber).then((m) => {
      if (m?.image) setImageUrl(m.image);
      setSauces(m?.sauces ?? null);
    }).catch(() => {});
  }, [menuIdAsNumber]);

  // Prefill from "Reorder last" — /statusOrder dispatches SETMENU before
  // navigating here, so we hydrate note + sauce (extracted from the prior
  // remark "Sauce: X — note") once on mount.
  useEffect(() => {
    if (!order || order.menu_id !== menuIdAsNumber) return;
    if (!order.remark || order.remark === "-") return;
    const m = order.remark.match(/^Sauce:\s*([^—]+?)(?:\s*—\s*(.*))?$/);
    if (m) {
      // Trust whatever sauce label was used previously — the menu's sauce
      // list may have been edited since (rename / removal). If the label
      // is no longer in the current list, the user can just pick again.
      const prior = m[1]?.trim();
      if (prior) setSauce(prior);
      if (m[2]) setNote(m[2].trim());
    } else {
      setNote(order.remark);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    dispatch({ type: "CLEAR" });
    navigate(backTo);
  };

  const handleContinue = async () => {
    if (!menuIdAsNumber) return;
    if (hasSauces && !sauce) {
      toast.error("Please pick a sauce");
      return;
    }
    setSubmitting(true);
    try {
      // Encode the sauce into the remark so the barista sees it on their card
      // — the order schema doesn't have a dedicated add-on column.
      const userNote = note.trim();
      const remark = hasSauces
        ? `Sauce: ${sauce}${userNote ? ` — ${userNote}` : ""}`
        : (userNote || "-");
      const result = await postOrder({
        menu_id: menuIdAsNumber,
        type: "dessert",
        sweetness: 0,
        remark,
        tumblerImage: "",
      });
      if (result.status === "ordered") {
        toast.success("Order placed");
        if (result.breakNotice) {
          toast.warn(result.breakNotice, { position: toast.POSITION.TOP_CENTER, autoClose: 5000 });
        }
        await new Promise((r) => setTimeout(r, 700));
        dispatch({ type: "CLEAR" });
        navigate(queueTo, { replace: true });
      }
    } catch (err: any) {
      console.error(err);
      // Server enforces "1 of each dessert per day" — show the message verbatim.
      const msg = err?.response?.data?.message;
      if (err?.response?.status === 409 && msg) {
        toast.error(msg);
      } else {
        toast.error("Order failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans pb-[120px]">
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-ink-100">
        <div className="px-3 h-[56px] flex items-center">
          <button
            type="button"
            onClick={handleCancel}
            aria-label="Back"
            className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-ink-1000 hover:bg-ink-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <p className="font-display font-bold text-base text-ink-1000 flex-1 text-center pr-[40px]">
            Customize
          </p>
        </div>
      </header>

      <main className="px-5 pt-[24px] max-w-md mx-auto">
        {/* Menu hero */}
        <div className="flex items-center gap-4 mb-[28px]">
          <div className="w-[80px] h-[80px] rounded-2xl bg-ink-100 flex items-center justify-center overflow-hidden flex-shrink-0">
            {imageUrl ? (
              <img src={ikThumb(imageUrl, 240, 240)} alt={menuName} className="w-full h-full object-cover" />
            ) : (
              <span className="font-display font-black text-3xl text-ink-300">
                {(menuName ?? "?").slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="eyebrow-v2 mb-1">Dessert</p>
            <h1 className="font-display font-black text-[24px] leading-tight tracking-tight text-ink-1000 break-words">
              {menuName}
            </h1>
          </div>
        </div>

        {/* Sauce — only shown when this menu has sauces configured by the
            barista on /edit-menu/:id (menus.sauces in the DB). */}
        {hasSauces && (
          <section className="mb-6">
            <span className="block font-display font-bold text-sm text-ink-1000 mb-2">
              Sauce <span className="text-rose-500">*</span>
            </span>
            <div className="grid grid-cols-3 gap-2">
              {sauces!.map((s) => {
                const active = sauce === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSauce(s)}
                    className={`h-[60px] rounded-2xl font-display font-bold text-sm border-2 transition active:scale-[0.97] ${
                      active
                        ? "bg-ink-1000 text-ink-0 border-ink-1000"
                        : "bg-ink-50 text-ink-700 border-transparent hover:border-ink-200"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-ink-500 mt-1.5">Pick one — sauce is required.</p>
          </section>
        )}

        {/* Note */}
        <section>
          <span className="block font-display font-bold text-sm text-ink-1000 mb-2">Note</span>
          <div className="relative">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={150}
              rows={4}
              className="w-full px-4 py-3 rounded-2xl bg-ink-50 border-2 border-transparent focus:border-ink-1000 focus:bg-white focus:outline-none text-base text-ink-1000 placeholder:text-ink-400 resize-none transition-colors"
            />
            <span className="absolute right-3 bottom-3 text-[11px] text-ink-400">
              {note.length}/150
            </span>
          </div>
        </section>
      </main>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 md:left-[280px] z-20 bg-white/95 backdrop-blur-md border-t border-ink-100 px-5 py-3">
        <div className="max-w-md mx-auto flex gap-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="btn-v2 flex-1 h-[52px] bg-ink-100 text-ink-1000 rounded-2xl"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={submitting}
            className="btn-v2-primary flex-[2] h-[52px] rounded-2xl"
          >
            {submitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25"/>
                  <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                </svg>
                Placing
              </>
            ) : (
              <>
                Continue
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                </svg>
              </>
            )}
          </button>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default FormOrderDessert;
